// components/FormatBar.js
import React, { useState, useRef } from 'react';
import { Bold, Italic, Code, List, Sparkles, Tag, GitBranch, Palette } from 'lucide-react';
import styles from './FormatBar.module.css';
import { useWorkspaces } from "./contexts/WorkspacesContext";
import useKeyPointsExtraction from '../hooks/useKeyPointsExtraction';
import ExtractionModal from './ExtractionModal';



const FloatingFormatBar = ({ editor, onSave, noteId }) => {
  const { getSelectedWorkspace, updateDocumentContent } = useWorkspaces(); 
  const { extractKeyPointsOnly, createStickiesFromResults, isProcessing } = useKeyPointsExtraction();
  const [showExtractionModal, setShowExtractionModal] = useState(false);
  const lastSelection = useRef(null);
  


  const ToolbarButton = ({ onClick, children, active, disabled }) => (
    <button
      type="button"
      onMouseDown={(e) => { 
        e.preventDefault(); 
        onClick?.(e); 
      }}
      className={`${styles.formatButton} ${active ? styles.active : ''}`}
      disabled={disabled}
    >
      {children}
    </button>
  );



const handleFormat = (format, value) => {
  if (!editor) return;
  
  // Try current selection first, fallback to last known selection
  let range = editor.getSelection(true);
  if (!range || range.length === 0) {
    range = lastSelection;
    if (range) {
      console.log('Using saved selection:', range);
      editor.setSelection(range.index, range.length);
      range = editor.getSelection();
    }
  }
  
  if (!range) return;
  
  if (format === 'code-block') {
    const currentFormat = editor.getFormat(range);
    editor.format('code-block', !currentFormat['code-block']);
  } else if (format === 'font') {
    // Handle font formatting
    if (range.length > 0) {
      editor.formatText(range.index, range.length, 'font', value);
    } else {
      editor.format('font', value);
    }
  } else if (format === 'size') {
    // Handle size formatting
    if (range.length > 0) {
      editor.formatText(range.index, range.length, format, value);
    } else {
      editor.format(format, value);
    }

      } else if (format === 'color') {
    // Handle color formatting
    if (range.length > 0) {
      editor.formatText(range.index, range.length, 'color', value);
    } else {
      editor.format('color', value);
    }
  } else if (range.length > 0) {
    // Text is selected - format the selection
    const current = editor.getFormat(range)[format];
    editor.formatText(range.index, range.length, format, !current);
  } else {
    // Cursor position - set typing format
    const current = editor.getFormat()[format];
    editor.format(format, !current);
  }

  // Force editor to update visually
  editor.focus();
  
  // Save content logic remains the same...
  setTimeout(() => {
    if (onSave && noteId) {
      // Floating editor context - use provided save function
      const content = editor.root.innerHTML;
      onSave(content);
    } else {
      // Main editor context - use existing WorkspacesContext
      const currentWorkspace = getSelectedWorkspace();
        if (editor && currentWorkspace) {
          const content = editor.root.innerHTML;
          updateDocumentContent(currentWorkspace.public_id, content);
        }
    }
  }, 10);
};
  
  // Function to handle key points extraction
  const handleExtractKeyPoints = async (options = {}) => {
    // Get the current note from context
    const currentWorkspace = getSelectedWorkspace();

      console.log('DEBUG - Editor available:', !!editor);
      console.log('DEBUG - Workspace available:', !!currentWorkspace);

      if (!editor || !currentWorkspace) {
        return { success: false, message: 'Editor or workspace not available in preview' };
      }

      const result = await extractKeyPointsOnly(options, editor, currentWorkspace.public_id);
    return result;
  };

  // Function to handle creating stickies from results
  const handleCreateStickies = async (aiData, extractionTypes) => {
        const currentWorkspace = getSelectedWorkspace();

    if (!currentWorkspace) {
      return { success: false, message: 'No workspace available for creating stickies' };
    }

    const result = await createStickiesFromResults(aiData, extractionTypes, currentWorkspace.public_id);
    return result;
  };

  return (
    <div className={styles.formatBar}>

      <div className={styles.formatBarSection}>
        <ToolbarButton onClick={() => handleFormat('bold')}>
          <Bold size={16} />
        </ToolbarButton>
        <ToolbarButton onClick={() => handleFormat('italic')}>
          <Italic size={16} />
        </ToolbarButton>
        <ToolbarButton onClick={() => handleFormat('code-block')}>
          <Code size={16} />
        </ToolbarButton>

<select 
  onChange={(e) => handleFormat('color', e.target.value)}
  className={styles.colorSelect}
  defaultValue=""
>
  <option value="" disabled>Color</option>
  <option value="#000000">Black</option>
  <option value="#ff0000">Red</option>
  <option value="#00ff00">Green</option>
  <option value="#0000ff">Blue</option>
  <option value="#ffff00">Yellow</option>
  <option value="#ff00ff">Magenta</option>
  <option value="#00ffff">Cyan</option>
  <option value="#808080">Gray</option>
  <option value="#800000">Dark Red</option>
  <option value="#008000">Dark Green</option>
  <option value="#000080">Dark Blue</option>
  <option value="#800080">Purple</option>
</select>
        <select 
  onChange={(e) => handleFormat('size', e.target.value)}
  className={styles.sizeSelect}
  defaultValue=""
>
  <option value="" disabled>Size</option>
  <option value="small">Small</option>
  <option value={false}>Normal</option>
  <option value="large">Large</option>
  <option value="huge">Huge</option>
</select>
            <select 
              onChange={(e) => handleFormat('font', e.target.value)}
              className={styles.fontSelect}
              defaultValue=""
            >
              <option value="" disabled>Font</option>
              <option value="sans-serif">Sans Serif</option>
              <option value="serif">Serif</option>
              <option value="monospace">Monospace</option>
              <option value="fira-sans">Fira Sans</option>
              <option value="playfair">Playfair Display</option>
              <option value="roboto">Roboto</option>
              <option value="oswald">Oswald</option>
              <option value="inconsolata">Inconsolata</option>
            </select>
      </div>

      <div className={styles.formatBarDivider} />

      <div className={styles.formatBarSection}>
        <ToolbarButton onClick={() => handleFormat('list', 'bullet')}>
          <List size={16} />
        </ToolbarButton>

      </div>
            
      {/* Add a spacer div to push the next section to the right */}
      <div style={{ flex: 1 }}></div>
      
      <div className={styles.formatBarDivider} />
      
      {/* Key Points extraction button on the far right */}
      <div className={styles.formatBarSection}>

        <ToolbarButton 
          onClick={() => setShowExtractionModal(true)} 
          disabled={isProcessing}
        >
          <Sparkles size={16} />
          <span style={{ marginLeft: '4px' }}>
            {isProcessing ? 'Extracting...' : 'Extract Key Points'}
          </span>
        </ToolbarButton>
      </div>

      {showExtractionModal && (
        <ExtractionModal 
          onClose={() => setShowExtractionModal(false)}
          onExtract={handleExtractKeyPoints}
          onCreateStickies={handleCreateStickies}
          isProcessing={isProcessing}
        />
      )}
    </div>
  );
};

export default FloatingFormatBar;